import { fetchAuthSession } from 'aws-amplify/auth';
import type { ApiResult } from '@keetcheck/shared';

const BASE = import.meta.env['VITE_API_BASE_URL'] ?? '/api';

async function getToken(): Promise<string> {
  const session = await fetchAuthSession();
  // ID token, not access token — email (and name) only
  // ever appear on the ID token for a standard Cognito user pool.
  const token = session.tokens?.idToken?.toString();
  if (!token) throw new Error('Not authenticated');
  return token;
}

// The unit the app is using (a person can be in several). Sent on every
// call; the API uses it when the route doesn't name a unit. Kept in step by
// AuthContext.
let currentOrgId: string | null = null;
export function setApiOrg(orgId: string | null): void { currentOrgId = orgId; }

/** The API's JSON envelope — or, for an error from in front of it (gateway
 *  timeout on a cold start, payload too large), a readable failure. Those
 *  can be JSON too (API Gateway's own `{"message": …}`), so check the shape:
 *  otherwise `json.error` is undefined and the toast comes up blank. */
async function readResult<T>(res: Response): Promise<T> {
  const json = await res.json().catch(() => null) as ApiResult<T> | null;
  const fallback = res.ok ? 'Unexpected response from the server' : `Server error (${res.status}) — try again`;
  if (!json || typeof json.ok !== 'boolean') throw new ApiError(fallback, res.status);
  if (!json.ok) throw new ApiError(json.error || fallback, res.status, json.code);
  return json.data;
}

async function request<T>(
  method: string,
  path: string,
  body?: unknown,
  orgId: string | null = currentOrgId,
): Promise<T> {
  const token = await getToken();
  const res = await fetch(`${BASE}${path}`, {
    method,
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
      ...(orgId ? { 'X-Org-Id': orgId } : {}),
    },
    body: body !== undefined ? JSON.stringify(body) : null,
  });

  return readResult<T>(res);
}

// Unauthenticated requests — for the public invite preview/claim endpoints,
// used by visitors who aren't signed in yet.
async function publicRequest<T>(
  method: string,
  path: string,
  body?: unknown,
): Promise<T> {
  const res = await fetch(`${BASE}${path}`, {
    method,
    headers: { 'Content-Type': 'application/json' },
    body: body !== undefined ? JSON.stringify(body) : null,
  });

  return readResult<T>(res);
}

export class ApiError extends Error {
  constructor(
    message: string,
    public readonly status: number,
    public readonly code?: string,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

export const api = {
  get:    <T>(path: string) => request<T>('GET', path),
  post:   <T>(path: string, body: unknown) => request<T>('POST', path, body),
  patch:  <T>(path: string, body: unknown) => request<T>('PATCH', path, body),
  put:    <T>(path: string, body: unknown) => request<T>('PUT', path, body),
  delete: <T>(path: string) => request<T>('DELETE', path),
};

/** Calls made for a given unit whatever unit the app is on now (changes
 *  saved offline are sent later, maybe after switching unit). */
export const apiFor = (orgId: string) => ({
  post:   <T>(path: string, body: unknown) => request<T>('POST', path, body, orgId),
  patch:  <T>(path: string, body: unknown) => request<T>('PATCH', path, body, orgId),
  delete: <T>(path: string) => request<T>('DELETE', path, undefined, orgId),
});

export const apiPublic = {
  get:  <T>(path: string) => publicRequest<T>('GET', path),
  post: <T>(path: string, body?: unknown) => publicRequest<T>('POST', path, body),
};
